import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DRAWER_STACK } from 'Utils/constants';
import { Fill } from 'nano-slots';
import ContactEditorDrawer from '../ContactEditorDrawer/ContactEditorDrawer';
import useDrawerStackRegister from 'Hooks/useDrawerStackRegister';
import InvoiceEditorDrawerFormFullData from './InvoiceEditorDrawerFormFullData';
import { scanInvoice, updateInvoice } from 'Services/invoices';
import InvoicePreviewFile from '../InvoicePreviewFile/InvoicePreviewFile';
import useInvoice from 'Hooks/useInvoice';
import Loading from 'Components/Atoms/Loading/Loading';
import Notice from 'Components/Atoms/Notice/Notice';
import useDrawerStack from 'Hooks/useDrawerStack';
import { useQueryClient } from '@tanstack/react-query';
import useInvoicesQuota from 'Hooks/useInvoicesQuota';

export default function InvoiceValidationDrawer(props) {
    const { onCancel, onClose, onRemoveFile, onUpload, invoiceId, onSubmit, onError } = props;

    const { invoice, isLoading } = useInvoice(invoiceId, {
        refetchOnMount: 'always',
        cacheTime: 0,
        staleTime: 0,
    });

    const { hasScanningQuota } = useInvoicesQuota();
    const { t } = useTranslation('invoices');
    const { open } = useDrawerStack();
    const [isRescanning, setIsRescanning] = useState(false);

    const queryClient = useQueryClient();

    const editHandle = async (data) => {
        try {
            const updatedInvoice = await updateInvoice(invoiceId, data);
            onSubmit(updatedInvoice);
        } catch (error) {
            onError(error);
        }
    };

    useDrawerStackRegister(DRAWER_STACK.CONTACT_EDITOR_WITH_INVOICE_PREVIEW, (props) => {
        return (
            <>
                <Fill name="drawer-stack-preview-desktop">
                    <InvoicePreviewFile id={invoiceId} onUpload={onUpload} onRemove={onRemoveFile} />
                </Fill>

                <ContactEditorDrawer {...props} />
            </>
        );
    });

    const handleDelete = () => {
        open(DRAWER_STACK.INVOICE_DELETE, {
            invoiceId,
            onSubmit: async () => {
                onClose();
            },
        });
    };

    const handleReScan = async () => {
        setIsRescanning(true);
        try {
            await scanInvoice(invoiceId);
            await queryClient.invalidateQueries({ queryKey: ['invoices'] });
        } catch (error) {
            onError(error);
        } finally {
            setIsRescanning(false);
            onClose();
        }
    };

    const showLoading = isLoading || !invoice;

    return (
        <>
            <Fill name="drawer-stack-preview-desktop">
                <InvoicePreviewFile id={invoiceId} onUpload={onUpload} onRemove={onRemoveFile} />
            </Fill>
            <Fill name="drawer-stack-preview-mobile">
                <div className="px-4 pb-3">
                    <InvoicePreviewFile id={invoiceId} onUpload={onUpload} onRemove={onRemoveFile} compactMode />
                </div>
            </Fill>
            {showLoading && (
                <div className="flex justify-center py-10">
                    <Loading />
                </div>
            )}
            {!showLoading && (
                <div className="h-full flex flex-col gap-2 mb-3">
                    {invoice.status === 'NOT_EMPTY' && <Notice type="warning">{t('no-empty-notice')}</Notice>}

                    {invoice.status === 'NO_QUOTA' && (
                        <Notice
                            type="warning"
                            action={
                                hasScanningQuota
                                    ? {
                                          onClick: handleReScan,
                                          isBusy: isRescanning,
                                          label: t('scan'),
                                      }
                                    : null
                            }
                        >
                            {t('no-quota-notice')}
                        </Notice>
                    )}

                    {invoice.status === 'NOT_INVOICE' && (
                        <Notice
                            type="warning"
                            action={{
                                label: t('delete'),
                                onClick: handleDelete,
                            }}
                        >
                            {t('not-invoice-notice')}
                        </Notice>
                    )}

                    {invoice.status === 'NOT_READABLE' && (
                        <Notice
                            type="warning"
                            action={{
                                label: t('delete'),
                                onClick: handleDelete,
                            }}
                        >
                            {t('invoice-not-readable-notice')}
                        </Notice>
                    )}

                    {invoice.status === 'ERROR' && (
                        <Notice
                            type="error"
                            action={
                                hasScanningQuota
                                    ? {
                                          onClick: handleReScan,
                                          isBusy: isRescanning,
                                          label: t('scan'),
                                      }
                                    : null
                            }
                        >
                            {t('invoice-error-notice')}
                        </Notice>
                    )}

                    <InvoiceEditorDrawerFormFullData
                        onCancel={onCancel}
                        onSubmit={editHandle}
                        invoice={invoice}
                        submitText={t('validate')}
                        onClose={onClose}
                        onError={onError}
                    />
                </div>
            )}
        </>
    );
}

InvoiceValidationDrawer.propTypes = {
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCancel: PropTypes.func,
    submitButtonText: PropTypes.string,
    onUpload: PropTypes.func,
    cancelButtonText: PropTypes.string,
    onRemoveFile: PropTypes.func,
    onError: PropTypes.func,
};
